/**
 * CareflowCard — Renders a single careflow entry with its nested hierarchy.
 *
 * Displays: name, description, entity badges, validation alerts, and
 * nested WorkflowCards. Provides actions for edit/delete and adding workflows.
 *
 * Props:
 *   careflow             — careflow data object (from getcareflows)
 *   careflowTypeId       — type (1=Default, 3=Device, 4=Resident, 5=Area, 6=Event)
 *   onEditCareflow       — callback to open careflow edit drawer
 *   onDeleteCareflow     — callback to open careflow delete dialog
 *   onAddWorkflow        — callback to add a workflow
 *   onEditWorkflow       — callback (workflow, wIndex) to edit a workflow
 *   onDeleteWorkflow     — callback (workflow, wIndex) to delete a workflow
 *   onAddEscalation      — callback (workflow, wIndex) to add an escalation
 *   onEditEscalation     — callback (workflow, wIndex, escalation, eIndex) to edit
 *   expandedEscalations  — expanded state map
 *   onToggleEscalation   — toggle callback
 *   campusSettings       — campus settings for output flags
 *   escMax               — max escalation period in seconds
 */

import WorkflowCard from './WorkflowCard.jsx';

// Check if workflows cover the entire day (0–86400)
function hasIncompleteDayCoverage(workflows) {
    if (!workflows || workflows.length === 0) return true;

    const sorted = [...workflows].sort((a, b) => a.window_start - b.window_start);
    const timeline = new Array(86400).fill(false);

    for (const w of sorted) {
        const s = w.window_start;
        const e = w.window_end;
        if (e >= s) {
            for (let i = s; i < e; i++) timeline[i] = true;
        } else {
            // Wraps around midnight
            for (let i = s; i < 86400; i++) timeline[i] = true;
            for (let i = 0; i < e; i++) timeline[i] = true;
        }
    }

    return timeline.includes(false);
}

const CareflowCard = ({
    careflow,
    careflowTypeId,
    onEditCareflow,
    onDeleteCareflow,
    onAddWorkflow,
    onEditWorkflow,
    onDeleteWorkflow,
    onAddEscalation,
    onEditEscalation,
    expandedEscalations,
    onToggleEscalation,
    campusSettings,
    escMax,
}) => {
    const hasWorkflows = careflow.workflows && careflow.workflows.length > 0;
    const hasEntities = careflow.entities && careflow.entities.length > 0;
    const incompleteDay = hasWorkflows && hasIncompleteDayCoverage(careflow.workflows);

    // Card border: danger if no workflows, warning if incomplete day or no entities (for type 4/5)
    let borderClass = '';
    if (!hasWorkflows) {
        borderClass = ' cf-card--danger';
    } else if (incompleteDay) {
        borderClass = ' cf-card--warning';
    } else if (!hasEntities && (careflowTypeId === 4 || careflowTypeId === 5)) {
        borderClass = ' cf-card--warning';
    }

    return (
        <div className={`cf-card${borderClass}`}>
            {/* Validation alerts */}
            {!hasWorkflows ? (
                <div className="cf-card__alert cf-card__alert--danger">
                    <svg className="cf-card__alert-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                    </svg>
                    No workflows set up on careflow
                </div>
            ) : null}
            {hasWorkflows && incompleteDay ? (
                <div className="cf-card__alert cf-card__alert--warning">
                    <svg className="cf-card__alert-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                    </svg>
                    Workflows do not cover the entire day
                </div>
            ) : null}
            {!hasEntities && (careflowTypeId === 4 || careflowTypeId === 5) ? (
                <div className="cf-card__alert cf-card__alert--warning">
                    <svg className="cf-card__alert-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                    </svg>
                    No entities attached to this careflow
                </div>
            ) : null}

            {/* Header: name + edit/delete */}
            <div className="cf-card__header">
                <div>
                    <h2 className="cf-card__title">{careflow.careflow_name}</h2>
                    {careflow.careflow_description ? (
                        <p className="cf-card__desc">{careflow.careflow_description}</p>
                    ) : null}
                </div>
                <div className="cf-card__actions">
                    <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={onEditCareflow}>
                        <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" style={{ marginRight: '0.25rem' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
                        </svg>
                        Edit
                    </button>
                    {careflowTypeId !== 1 ? (
                        <button className="dp-btn dp-btn--danger dp-btn--sm" onClick={onDeleteCareflow}>
                            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" style={{ marginRight: '0.25rem' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                            Delete
                        </button>
                    ) : null}
                </div>
            </div>

            {/* Entity badges */}
            {hasEntities ? (
                <div className="cf-entity-badges" style={{ marginBottom: 'var(--space-3, 0.75rem)' }}>
                    {careflow.entities.map((entity, i) => (
                        <span key={entity.reference_id || i} className="cf-entity-badge">
                            {entity.reference_name}
                        </span>
                    ))}
                </div>
            ) : null}

            {/* Workflows section */}
            {hasWorkflows ? (
                <div className="cf-workflow-section">
                    <h3 className="cf-workflow-section__title">
                        <svg className="cf-workflow-section__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                        Workflows
                    </h3>
                    {careflow.workflows.map((w, wIndex) => (
                        <WorkflowCard
                            key={w.workflow_id || wIndex}
                            workflow={w}
                            careflowTypeId={careflowTypeId}
                            onEdit={() => onEditWorkflow(w, wIndex)}
                            onDelete={() => onDeleteWorkflow(w, wIndex)}
                            onAddEscalation={() => onAddEscalation(w, wIndex)}
                            onEditEscalation={(esc, eIndex) => onEditEscalation(w, wIndex, esc, eIndex)}
                            expandedEscalations={expandedEscalations}
                            onToggleEscalation={onToggleEscalation}
                            campusSettings={campusSettings}
                            escMax={escMax}
                        />
                    ))}
                </div>
            ) : null}

            {/* Add Workflow button */}
            <div className="cf-card__footer">
                <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={onAddWorkflow}>
                    <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" style={{ marginRight: '0.25rem' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Workflow
                </button>
            </div>
        </div>
    );
};

export default CareflowCard;
